// HTTP client for /api/consultants (profile domain).
// Mirrors src/lib/fs/profile.ts signatures.
//
// Binary storage (human_master_cv.docx) is punted to a later session; the
// client-side text extraction still runs, and the extracted plain text is
// stored server-side on the consultant row as `master_cv_text`.
import type { Profile } from "../fs/profile";
import { api } from "./_client";

interface ConsultantRow {
  id: string;
  folder: string;
  name: string;
  email: string | null;
  entra_oid: string | null;
  profile: Profile | null;
  master_cv_text: string | null;
  system_master_cv: Record<string, unknown> | null;
  stack_drawer_id: string | null;
  active: boolean;
  non_consulting: boolean;
  created_at: string;
  updated_at: string;
}

export async function checkMasterCvExists(
  _repoPath: string,
  folderName: string,
): Promise<boolean> {
  try {
    const row = await api<ConsultantRow>(
      "GET",
      `/api/consultants/${encodeURIComponent(folderName)}`,
    );
    return !!row.master_cv_text && row.master_cv_text.length > 0;
  } catch {
    return false;
  }
}

// Signature preserved from fs/profile.ts. `docxData` is ignored in v2.0 for
// now (blob storage integration is a later session); `plainText` becomes the
// authoritative CV text.
export async function saveMasterCv(
  _repoPath: string,
  folderName: string,
  _docxData: ArrayBuffer,
  plainText: string,
): Promise<void> {
  await api<ConsultantRow>(
    "PUT",
    `/api/consultants/${encodeURIComponent(folderName)}`,
    { master_cv_text: plainText },
  );
}

export async function readProfileJson(
  _repoPath: string,
  folderName: string,
): Promise<Profile | null> {
  try {
    const row = await api<ConsultantRow>(
      "GET",
      `/api/consultants/${encodeURIComponent(folderName)}`,
    );
    return row.profile;
  } catch {
    return null;
  }
}

export async function writeProfileJson(
  _repoPath: string,
  folderName: string,
  profile: Profile,
): Promise<void> {
  await api<ConsultantRow>(
    "PUT",
    `/api/consultants/${encodeURIComponent(folderName)}`,
    { profile },
  );
}

export async function listAllCandidates(
  _repoPath?: string,
): Promise<{ folder: string; name: string }[]> {
  try {
    const rows = await api<ConsultantRow[]>("GET", "/api/consultants");
    return rows.map((r) => ({ folder: r.folder, name: r.name }));
  } catch (err) {
    console.error("listAllCandidates failed", err);
    return [];
  }
}

export async function createCandidate(
  _repoPath: string,
  folderName: string,
  fullName: string,
  email = "",
): Promise<void> {
  await api<ConsultantRow>("POST", "/api/consultants", {
    folder: folderName,
    name: fullName,
    email,
  });
}

// Returns the best available CV text for AI matching. In v2.0 that's just
// the master_cv_text field on the consultant row; consolidation output
// lives inside the same row as system_master_cv (JSON) and is serialised
// here into markdown-ish plain text to preserve v1.7 caller expectations.
export async function readBestCvText(
  _repoPath: string,
  folderName: string,
): Promise<string> {
  const row = await api<ConsultantRow>(
    "GET",
    `/api/consultants/${encodeURIComponent(folderName)}`,
  );
  if (row.system_master_cv) {
    return serialiseCvToText(row.system_master_cv);
  }
  return row.master_cv_text ?? "";
}

function serialiseCvToText(cv: any): string {
  const lines: string[] = [];
  if (cv.role_title) lines.push(`# ${cv.role_title}`, "");
  if (cv.profile_text) lines.push("## Profiel", cv.profile_text, "");
  if (cv.competencies?.length) {
    lines.push("## Competenties");
    for (const c of cv.competencies) lines.push(`- ${c}`);
    lines.push("");
  }
  if (cv.tech_stack && Object.keys(cv.tech_stack).length) {
    lines.push("## Tech Stack");
    for (const [cat, techs] of Object.entries(cv.tech_stack)) {
      lines.push(`### ${cat}`, `${techs}`);
    }
    lines.push("");
  }
  if (cv.roles?.length) {
    lines.push("## Werkervaring");
    for (const r of cv.roles) {
      lines.push(`### ${r.title} @ ${r.organisation} (${r.period})`);
      if (r.description) lines.push(r.description);
      for (const b of r.bullets ?? []) lines.push(`- ${b}`);
      if (r.result) lines.push(`Resultaat: ${r.result}`);
      if (r.tech) lines.push(`Tech: ${r.tech}`);
      lines.push("");
    }
  }
  if (cv.education?.length) {
    lines.push("## Opleiding");
    for (const e of cv.education) lines.push(`- ${e.degree}, ${e.institution} (${e.year})`);
    lines.push("");
  }
  if (cv.certifications?.length) {
    lines.push("## Certificaten");
    for (const c of cv.certifications) lines.push(`- ${c}`);
    lines.push("");
  }
  return lines.join("\n");
}

// Re-export the Profile type so callers importing from api/profile get the
// same interface surface as they would from fs/profile.
export type {
  ConfirmedGap,
  ConfirmedSkill,
  InferredSkill,
  Profile,
  ProfilePersonal,
} from "../fs/profile";
